import { useState, useEffect } from "react";
import axios from "axios";
import {
  Search, Plus, Loader2, Users, BookOpen, List, UserPlus,
  Edit2, Trash2, X, Check, Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function MailContacts() {
  const [contacts, setContacts] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [distLists, setDistLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [contactDialog, setContactDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [listDialog, setListDialog] = useState(false);

  useEffect(() => {
    fetchContacts();
    fetchDirectory();
    fetchDistLists();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/mail/contacts`, {
        headers: getHeaders(), withCredentials: true,
      });
      setContacts(res.data.contacts || []);
    } catch {}
    setLoading(false);
  };

  const fetchDirectory = async () => {
    try {
      const res = await axios.get(`${API}/mail/directory`, {
        headers: getHeaders(), withCredentials: true,
      });
      setDirectory(res.data.directory || []);
    } catch {}
  };

  const fetchDistLists = async () => {
    try {
      const res = await axios.get(`${API}/mail/distribution-lists`, {
        headers: getHeaders(), withCredentials: true,
      });
      setDistLists(res.data.distribution_lists || []);
    } catch {}
  };

  const handleCreateContact = async (data) => {
    try {
      await axios.post(`${API}/mail/contacts`, data, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Contact created");
      setContactDialog(false);
      fetchContacts();
    } catch {
      toast.error("Failed to create contact");
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      await axios.delete(`${API}/mail/contacts/${id}`, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Contact deleted");
      fetchContacts();
    } catch {
      toast.error("Failed to delete contact");
    }
  };

  const filteredContacts = contacts.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(q) ||
      c.last_name?.toLowerCase().includes(q) ||
      c.email?.some((e) => e.toLowerCase().includes(q))
    );
  });

  const filteredDirectory = directory.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.name?.toLowerCase().includes(q) || d.email?.toLowerCase().includes(q);
  });

  return (
    <div className="p-4">
      <Tabs defaultValue="contacts" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="rounded-none border border-slate-200 bg-white">
            <TabsTrigger value="contacts" className="text-xs rounded-none data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700">
              <Users className="w-3.5 h-3.5 mr-1" /> My Contacts
            </TabsTrigger>
            <TabsTrigger value="directory" className="text-xs rounded-none data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700">
              <BookOpen className="w-3.5 h-3.5 mr-1" /> Org Directory
            </TabsTrigger>
            <TabsTrigger value="lists" className="text-xs rounded-none data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700">
              <List className="w-3.5 h-3.5 mr-1" /> Distribution Lists
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..." className="pl-7 h-8 text-xs rounded-none w-48"
              />
            </div>
          </div>
        </div>

        <TabsContent value="contacts">
          <div className="flex justify-end mb-3">
            <Dialog open={contactDialog} onOpenChange={setContactDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500">
                  <Plus className="w-3 h-3 mr-1" /> Add Contact
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="text-sm">New Contact</DialogTitle>
                </DialogHeader>
                <ContactForm onSubmit={handleCreateContact} onCancel={() => setContactDialog(false)} />
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto py-8" />
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No contacts found</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredContacts.map((contact) => (
                <div key={contact.id} className="bg-white border border-slate-200 p-3 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-sm font-semibold text-violet-700 flex-shrink-0">
                    {(contact.first_name || contact.last_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {contact.first_name} {contact.last_name}
                    </p>
                    {contact.email?.[0] && (
                      <p className="text-xs text-slate-400 truncate">{contact.email[0]}</p>
                    )}
                    {contact.department && (
                      <p className="text-[10px] text-slate-500">{contact.department}</p>
                    )}
                    <div className="flex gap-1 mt-1">
                      <button onClick={() => handleDeleteContact(contact.id)} className="text-slate-300 hover:text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="directory">
          {filteredDirectory.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No directory entries found</div>
          ) : (
            <div className="bg-white border border-slate-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-app border-b border-slate-200">
                    <th className="text-left px-3 py-2 font-medium text-slate-500">Name</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-500">Email</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-500">Department</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-500">Position</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredDirectory.map((person) => (
                    <tr key={person.user_id} className="hover:bg-app">
                      <td className="px-3 py-2 font-medium text-slate-800">{person.name}</td>
                      <td className="px-3 py-2 text-slate-500">{person.email}</td>
                      <td className="px-3 py-2 text-slate-500">{person.department || "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{person.position || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="lists">
          <div className="flex justify-end mb-3">
            <Dialog open={listDialog} onOpenChange={setListDialog}>
              <DialogTrigger asChild>
                <Button size="sm" className="h-8 text-xs rounded-none bg-violet-600 hover:bg-violet-500">
                  <Plus className="w-3 h-3 mr-1" /> Create List
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="text-sm">New Distribution List</DialogTitle>
                </DialogHeader>
                <DistListForm onSubmit={async (data) => {
                  try {
                    await axios.post(`${API}/mail/distribution-lists`, data, {
                      headers: getHeaders(), withCredentials: true,
                    });
                    toast.success("Distribution list created");
                    setListDialog(false);
                    fetchDistLists();
                  } catch { toast.error("Failed to create list"); }
                }} onCancel={() => setListDialog(false)} />
              </DialogContent>
            </Dialog>
          </div>

          {distLists.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No distribution lists</div>
          ) : (
            <div className="space-y-2">
              {distLists.map((list) => (
                <div key={list.id} className="bg-white border border-slate-200 p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{list.name}</p>
                    <p className="text-xs text-slate-400">{list.members?.length || 0} members</p>
                  </div>
                  <Button variant="ghost" size="sm" className="h-6 text-xs text-red-500"
                    onClick={async () => {
                      await axios.delete(`${API}/mail/distribution-lists/${list.id}`, {
                        headers: getHeaders(), withCredentials: true,
                      });
                      fetchDistLists();
                      toast.success("List deleted");
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ContactForm({ onSubmit, onCancel, initial }) {
  const [data, setData] = useState(initial || { first_name: "", last_name: "", email: [], phone: [], department: "", position: "", company: "", notes: "" });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="First name" value={data.first_name} onChange={(e) => setData({ ...data, first_name: e.target.value })}
          className="h-8 text-xs rounded-none" />
        <Input placeholder="Last name" value={data.last_name} onChange={(e) => setData({ ...data, last_name: e.target.value })}
          className="h-8 text-xs rounded-none" />
      </div>
      <Input placeholder="Email" value={data.email[0] || ""} onChange={(e) => setData({ ...data, email: [e.target.value] })}
        className="h-8 text-xs rounded-none" />
      <Input placeholder="Department" value={data.department} onChange={(e) => setData({ ...data, department: e.target.value })}
        className="h-8 text-xs rounded-none" />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-8 text-xs rounded-none">Cancel</Button>
        <Button size="sm" onClick={() => onSubmit(data)} className="h-8 text-xs rounded-none bg-violet-600">Save</Button>
      </div>
    </div>
  );
}

function DistListForm({ onSubmit, onCancel }) {
  const [name, setName] = useState("");
  return (
    <div className="space-y-3">
      <Input placeholder="List name" value={name} onChange={(e) => setName(e.target.value)}
        className="h-8 text-xs rounded-none" />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel} className="h-8 text-xs rounded-none">Cancel</Button>
        <Button size="sm" onClick={() => onSubmit({ name, members: [] })} className="h-8 text-xs rounded-none bg-violet-600"
          disabled={!name.trim()}>Create</Button>
      </div>
    </div>
  );
}
